<template lang="pug">
.position-relative
  .invoice-popover(v-if="$route.query.invoice_id")
    ShowPurchaseInvoice(:id="$route.query.invoice_id")
  template(v-else)
    UiCardSection
      UiStack
        UiStackItem(fill)
          .ui-card-section-title {{ $t("companies.tabs.automation") }}
    UiCardSection
      RestTable(
        namespace="automation_list"
        :headers="headers"
        ref="automationTable"
        :fetch-handler="getData"
        :initial-filters="initialFilters"
        embed
      )
        template(#filters="{ filters, updateFilters }")
          UiStack(vertical)
            UiStack
              UiStackItem(fill)
                UiFormGroup(
                  :label="$t('companies.automation.bill_id')"
                  label-for="netvisor-key-input"
                )
                  UiInput#netvisor-key-input(
                    :modelValue="filters.remote_key"
                    type="search"
                    @update:modelValue="updateFilters({ remote_key: $event })"
                  )
              UiStackItem(fill)
                UiFormGroup(
                  :label="$t('companies.automation.remote_key')"
                  label-for="invoice-key-input"
                  label-cols="4"
                )
                  UiInput#invoice-key-input(
                    :modelValue="filters.bill_id"
                    :allowClear="true"
                    @update:modelValue="updateFilters({ bill_id: $event })"
                  )
              UiStackItem(fill)
                UiFormGroup(:label="$t('companies.automation.start_date')")
                  UiDatePicker#start-date-input(
                    :modelValue="new Date(filters.start_date)"
                    @update:modelValue="updateFilters({ start_date: startOfDay($event) })"
                  )
              UiStackItem(fill)
                UiFormGroup(
                  :label="$t('companies.automation.end_date')"
                  label-for="end-date-input"
                )
                  UiDatePicker#end-date-input(
                    :modelValue="new Date(filters.end_date)"
                    @update:modelValue="updateFilters({ end_date: endOfDay($event) })"
                  )
            UiStack(spacing="xl")
              UiStackItem
                UiCheckbox(
                  :modelValue="filters.suggested"
                  switch
                  @update:modelValue="updateFilters({ suggested: $event })"
                ) {{ $t("companies.automation.suggested") }}
              UiStackItem
                UiCheckbox(
                  :modelValue="filters.with_voucher"
                  switch
                  @update:modelValue="updateFilters({ with_voucher: $event })"
                ) {{ $t("companies.automation.with_voucher") }}
              UiStackItem
                UiCheckbox(
                  :modelValue="filters.wrong_dimensions"
                  switch
                  @update:modelValue="updateFilters({ wrong_dimensions: $event })"
                ) {{ $t("automation.wrong_dimensions_invoices") }}
              UiStackItem
                UiCheckbox(
                  :modelValue="filters.wrong_accounts"
                  switch
                  @update:modelValue="updateFilters({ wrong_accounts: $event })"
                ) {{ $t("automation.wrong_accounts_invoices") }}
        template(#cell:remote_key="{ item }")
          RouterLink(
            :to="{ ...$route, query: { ...$route.query, invoice_id: item.id.toString() } }"
            label="item.remote_key"
          )
            | {{ item.remote_key }}
        template(#cell:status="{ item }")
          ItemStatusBadge(:item="item")
        template(#cell:account_suggest_status="{ item }")
          AutomationStatusBadge(:item="item.account_suggest_status")
        template(#cell:dimension_suggest_status="{ item }")
          AutomationStatusBadge(:item="item.dimension_suggest_status")
        template(#cell:approval_status="{ item }")
          AutomationStatusBadge(:item="item.approval_status")
        template(#cell:actions="{ item }")
          UiStack(spacing="xs")
            UiStackItem
              UiButton(
                size="sm"
                variant="smooth-success"
                @click="resuggest(item)"
                v-if="(['not_suggested', 'failed'].includes(item.status) || item.approval_status == 'not_suggested') && item.with_voucher == 'NO'"
              )
                .mdi.mdi-restart
                | {{ $t("companies.automation.resuggest") }}
</template>

<script>
import ApiCompanies from "@/api/companies.js";
import ApiPurchaseInvoices from "@/api/purchase_invoices.js";
import ShowPurchaseInvoice from "@/views/PurchaseInvoice/show.vue";
import ItemStatusBadge from "./automation/item-status-badge.vue";
import AutomationStatusBadge from "./automation/automation-status-badge.vue";
import { addMonths } from "@/common/date_utils";
import { endOfDay, startOfDay } from "@bi-book/bibook-ui-kit";

export default {
  components: {
    ShowPurchaseInvoice,
    ItemStatusBadge,
    AutomationStatusBadge,
  },
  data() {
    const startDate = startOfDay(addMonths(new Date(), -1));
    const endDate = endOfDay(new Date());

    return {
      initialFilters: {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        sort_order: 'desc',
      },
      headers: [
        {
          key: "remote_key",
          label: "Bill ID",
          sortable: true,
          squeeze: true,
        },
        {
          key: "bill_id",
          label: "Invoice Number",
          sortable: true,
          squeeze: true,
        },
        {
          key: "vendor_name",
          label: "Vendor name",
          sortable: true,
          squeeze: true,
        },
        {
          key: "date",
          label: "Date",
          sortable: true,
          squeeze: true,
        },
        {
          key: "total_sum",
          label: "Total sum",
          sortable: true,
          squeeze: true,
        },
        {
          key: "with_voucher",
          label: "With Voucher?",
          sortable: false,
          squeeze: true,
        },
        {
          key: "account_correct",
          label: "Correct #",
          sortable: true,
          squeeze: true,
        },
        {
          key: "correct_percent",
          label: "Correct %",
          sortable: true,
          squeeze: true,
        },
        {
          key: "suggestion_methods",
          label: "Methods",
          sortable: true,
          squeeze: true,
        },
        {
          key: "status",
          label: "Invoice Status",
          sortable: true,
          squeeze: true,
        },
        {
          key: "account_suggest_status",
          label: "Status Account",
          sortable: false,
          squeeze: true,
        },
        {
          key: "dimension_suggest_status",
          label: "Status Dimension",
          sortable: true,
          squeeze: true,
        },
        {
          key: "approval_status",
          label: "Status Circulation",
          sortable: true,
          squeeze: true,
        },
        {
          key: "comment",
          label: "Comment",
          sortable: true,
          squeeze: true,
        },
        {
          key: "actions",
          label: "",
          squeeze: true,
        },
      ],
    };
  },
  methods: {
    startOfDay: startOfDay,
    endOfDay: endOfDay,

    reload() {
      this.$refs.automationTable.fetchData();
    },

    getData(params, config) {
      return ApiCompanies.fetchAutomation(
        this.$route.params.id,
        params,
        config,
      ).then((resp) => {
        return {
          items: resp.data.invoices,
          meta: resp.data.meta,
        };
      });
    },

    resuggest(item) {
      let params = { purchase_invoice: { status: "new" } };
      ApiPurchaseInvoices.updateInvoice(item.id, params).then((resp) => {
        this.reload();
      });
    },
  },
};
</script>
